// Save a GENERATED logo set (from /ai/logo-generate) to a real brand — the SAME pipeline
// PremiumLogoPanel uses, so a logo designed in the chat interview lands in the Brand Manager's logo
// slots (primary / dark / icon) identically. Background removed + padding trimmed to transparency in
// the browser, with a raw-image fallback if that fails. No second source of truth.

import { uploadLogo, type LogoVariant } from '@/api/brands';
import { base64ToFile } from '@/lib/brandMonogram';
import { trimTransparentPadding } from '@/lib/imageTrim';
import type { GeneratedLogoSet, LogoImage } from '@/api/ai';

export const logoDataUri = (img: LogoImage): string => `data:${img.mimeType};base64,${img.image}`;
const extFor = (mime: string) => (mime.includes('png') ? 'png' : mime.includes('webp') ? 'webp' : 'jpg');

/**
 * Upload a generated logo set to `brandId`. Each variant's background is removed + trimmed to
 * transparency (the @imgly model loads once, lazily), falling back to the raw generated image if
 * removal fails. Best-effort per variant — a failure never throws (the build/brand still succeeds).
 */
export async function uploadGeneratedLogoSet(
  brandId: string,
  set: GeneratedLogoSet,
  safeName: string,
): Promise<void> {
  const { removeBackground } = await import('@imgly/background-removal');
  const saveOne = async (img: LogoImage, variant: LogoVariant, name: string) => {
    let file: File;
    try {
      const blob = await removeBackground(logoDataUri(img));
      const trimmed = await trimTransparentPadding(blob);
      file = new File([trimmed], `${name}.png`, { type: 'image/png' });
    } catch {
      file = base64ToFile(img.image, img.mimeType, `${name}.${extFor(img.mimeType)}`);
    }
    await uploadLogo(brandId, variant, file);
  };
  // Sequential — the background-removal model is shared and shouldn't run three times at once.
  if (set.primary) await saveOne(set.primary, 'primary', safeName).catch(() => {});
  if (set.dark) await saveOne(set.dark, 'dark', `${safeName}-reversed`).catch(() => {});
  if (set.icon) await saveOne(set.icon, 'icon', `${safeName}-favicon`).catch(() => {});
}
